import { useRouter } from 'next/router';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';

import {
  CompanyFilters,
  EventFilters,
  FilterableField,
  FilterableFieldSettings,
  InternshipFilters,
  JobFilters,
  JobsAndInternshipFilter,
  SocietyFilters,
} from 'hooks/useGlobalSearch/useGlobalSearchInternal';
import { useResponsive } from 'hooks/useResponsive';
import { SearchPageSortBy, SearchPageType } from 'lib/utils/global.search';
import { TouchpointType } from 'lib/models/touchpoint';
import { apiInstance } from 'lib/utils/axios';
import { BasicOpportunitiesResponse, BasicOpportunity, OpportunityResponse } from 'lib/models/opportunity';
import { API_ROUTES } from 'lib/api-routes';
import {
  getAxiosPaginatedResponseTotal,
  getAxiosPaginatedResponseTotalV2,
  getAxiosResponseTotalV2,
  parseArrayResponse,
  parseAxiosArrayResponseV2,
  parseAxiosPaginatedResponse,
  parseAxiosPaginatedResponseV2,
  parseResponse,
} from 'lib/utils/parser';
import { BasicStudentSocietiesResponse, TBasicStudentSociety } from 'lib/models/student-society';
import { BasicCompaniesResponse } from 'lib/models/company';
import { useAuth } from 'lib/providers/AuthProvider';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { FollowingResponse } from 'lib/models/following';

const PER_PAGE_COUNT_VERTICAL = 10;
const PER_PAGE_COUNT_GRID = 12;

type OpportunityAttributesUpdateRecord = {
  following?: FollowingResponse;
  is_applied?: boolean;
  is_waitlisted?: boolean;
};

export const useGlobalSearchDataInternal = ({ type, pageTitle }: { type: SearchPageType; pageTitle?: string }) => {
  const { query } = useRouter();
  const { candidateUniversity, isCandidate, isInitialAuthLoading } = useAuth();
  const screens = useResponsive();
  const { partialFilter, debouncedSearch, sortBy, initialQueryParamsReplaceLoading } = useGlobalSearch();
  const candidateUniversityId = candidateUniversity?.id || null;

  const [currentOpportunityIndex, setCurrentOpportunityIndex] = useState<number>(0);

  const { university_ids } = query;

  function parseFilterKeyValue({ key, value }: { key: string; value: string | string[] | null | undefined }) {
    let parsedKey = key,
      newValue = value;

    switch (key) {
      case FilterableFieldSettings[FilterableField.POSTED_BY_COMPANIES].param:
      case FilterableFieldSettings[FilterableField.POSTED_BY_ORGANIZATIONS].param:
        parsedKey = 'creatable_for_ids';
        break;
      case FilterableFieldSettings[FilterableField.EASY_REGISTER].param:
        parsedKey = 'apply_url_available';
        newValue = newValue === 'true' ? 'false' : null;
        break;
      case FilterableFieldSettings[FilterableField.QUICK_APPLY].param:
        parsedKey = 'is_eligible_for_auto_apply';
        break;
      case FilterableFieldSettings[FilterableField.EVENT_TYPES].param:
      case FilterableFieldSettings[FilterableField.INTERNSHIP_TYPES].param:
        parsedKey = 'kinds';
        break;
    }

    newValue = Array.isArray(newValue) && newValue.length === 2 && newValue.includes('true') && newValue.includes('false') ? null : newValue;
    const parsedValue = Array.isArray(newValue) && newValue.length === 1 ? newValue[0] : newValue;
    return { parsedKey, parsedValue };
  }

  const getFilterQueryString = ({
    fields,
    pageIndex,
    per_page,
    touchpointable_type,
    partial = false,
    filters,
    id_list_ids,
  }: {
    fields: Array<FilterableField>;
    pageIndex: number | null;
    per_page: number;
    touchpointable_type?: TouchpointType;
    partial: boolean;
    filters: Record<string, string | string[] | boolean | null>;
    id_list_ids?: string | string[];
  }) => {
    const param_fields = fields.flatMap((filter) => {
      const field = FilterableFieldSettings[filter];
      return field.subParam ? [field.param, field.subParam] : [field.param];
    });

    let params: Record<string, string | string[] | number | boolean | null | undefined> = {
      q: debouncedSearch,
      page: pageIndex === null ? undefined : partial ? 1 : pageIndex + 1,
      per_page: pageIndex === null ? 1000 : partial ? 1 : per_page,
      touchpointable_type,
      id_list_ids,
    };

    param_fields.forEach((field) => {
      const { parsedKey, parsedValue } = parseFilterKeyValue({ key: field, value: query[field] });
      params[parsedKey] = parsedValue;
    });

    if (partial && partialFilter) {
      Object.entries(partialFilter).forEach(([key, value]) => {
        const { parsedKey, parsedValue } = parseFilterKeyValue({ key, value });
        params[parsedKey] = parsedValue;
      });
    }

    if (filters) {
      params = { ...params, ...filters };
    }

    const qs = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
      skipNull: true,
    });

    return qs;
  };

  const getOpportunitiesFilterParamsKey = (
    pageIndex: number | null,
    partial: boolean,
    filters: Record<string, string | string[] | boolean | null> = {}
  ) => {
    if (initialQueryParamsReplaceLoading || isInitialAuthLoading) return null;
    let qs = '';

    switch (type) {
      case SearchPageType.JOBS:
        qs = getFilterQueryString({
          fields: JobFilters,
          pageIndex,
          per_page: PER_PAGE_COUNT_VERTICAL,
          touchpointable_type: TouchpointType.Job,
          partial,
          id_list_ids: query?.id_list_ids,
          filters: {
            sort_by: 'posted_at',
            sort_order: 'desc',
            ...filters,
          },
        });
        break;
      case SearchPageType.JOBS_AND_INTERNSHIPS:
        qs = getFilterQueryString({
          fields: JobsAndInternshipFilter,
          pageIndex,
          per_page: PER_PAGE_COUNT_VERTICAL,
          touchpointable_type: TouchpointType.Job,
          partial,
          filters: {
            sort_by: 'posted_at',
            sort_order: 'desc',
            ...filters,
          },
        });
        break;
      case SearchPageType.INTERNSHIPS:
        qs = getFilterQueryString({
          fields: InternshipFilters,
          pageIndex,
          per_page: PER_PAGE_COUNT_VERTICAL,
          touchpointable_type: TouchpointType.Internship,
          partial,
          id_list_ids: query?.id_list_ids,
          filters: {
            sort_by: 'posted_at',
            sort_order: 'desc',
            ...filters,
          },
        });
        break;
      case SearchPageType.EVENTS:
        qs = getFilterQueryString({
          fields: EventFilters,
          pageIndex,
          per_page: PER_PAGE_COUNT_VERTICAL,
          touchpointable_type: TouchpointType.Event,
          partial,
          filters: {
            sort_by: 'posted_at',
            sort_order: 'desc',
            ...filters,
          },
        });
        break;
    }

    return qs ? `${API_ROUTES.BASIC_TOUCHPOINTS_V2}?${qs}` : null;
  };

  const getSocietiesFilterParamsKey = (
    pageIndex: number | null,
    partial: boolean,
    filters: Record<string, string | string[] | boolean | null> = {}
  ) => {
    if (type !== SearchPageType.COMMUNITIES || isInitialAuthLoading) return;
    const qs = getFilterQueryString({ fields: SocietyFilters, pageIndex, per_page: PER_PAGE_COUNT_GRID, partial, filters });
    return `${API_ROUTES.STUDENT_SOCIETIES}?basic_details=true&with_icon=true&${qs}`;
  };

  const getCompaniesFilterParamsKey = (pageIndex: number, partial: boolean, filters: Record<string, string | string[] | boolean | null> = {}) => {
    if (type !== SearchPageType.COMPANIES || isInitialAuthLoading) return;
    const qs = getFilterQueryString({ fields: CompanyFilters, pageIndex, per_page: PER_PAGE_COUNT_GRID, partial, filters });
    return `${API_ROUTES.COMPANIES}?with_icon=true&${qs}`;
  };

  const { data: featuredOpportunitiesResponse, isLoading: isFeaturedOpportunitiesLoading } = useSWR<AxiosResponse<BasicOpportunitiesResponse>>(
    () =>
      sortBy === SearchPageSortBy.MOST_RELEVANT
        ? getOpportunitiesFilterParamsKey(null, false, { featured: true, from_matches: isCandidate ? false : null })
        : null,
    apiInstance.get,
    { revalidateOnFocus: false }
  );
  const { data: matchesOpportunitiesResponse, isLoading: isMatchesOpportunitiesLoading } = useSWR<AxiosResponse<BasicOpportunitiesResponse>>(
    () =>
      isCandidate && sortBy === SearchPageSortBy.MOST_RELEVANT
        ? getOpportunitiesFilterParamsKey(null, false, { featured: false, from_matches: true })
        : null,
    apiInstance.get,
    { revalidateOnFocus: false }
  );
  const { data: universityEventsResponse, isLoading: isUniversityEventsLoading } = useSWR<AxiosResponse<BasicOpportunitiesResponse>>(
    () =>
      type === SearchPageType.EVENTS && candidateUniversityId && sortBy === SearchPageSortBy.MOST_RELEVANT
        ? getOpportunitiesFilterParamsKey(null, false, { featured: false, from_matches: false, university_ids: candidateUniversityId })
        : null,
    apiInstance.get,
    { revalidateOnFocus: false }
  );

  const {
    data: opportunitiesResponse,
    isLoading: isOpportunitiesLoading,
    isValidating: isOpportunitiesValidating,
    size: opportunitiesSize,
    setSize: setOpportunitiesSize,
  } = useSWRInfinite<AxiosResponse<BasicOpportunitiesResponse>>(
    (pageIndex) => {
      let filters: Record<string, string | boolean | string[] | null> = {};
      if (sortBy === SearchPageSortBy.MOST_RELEVANT) {
        filters = {
          featured: false,
          from_matches: isCandidate ? false : null,
          except_university_ids: type === SearchPageType.EVENTS ? candidateUniversityId : null,
        };
      } else if (sortBy === SearchPageSortBy.CLOSING_SOONEST) {
        filters = {
          sort_by: 'CASE WHEN application_deadline IS NOT NULL THEN 0 ELSE 1 END, application_deadline ASC NULLS LAST,',
          sort_order: 'posted_at DESC',
        };
      }
      return getOpportunitiesFilterParamsKey(pageIndex, false, filters);
    },
    apiInstance.get,
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    }
  );

  const { data: partialOpportunitiesResponse, isLoading: isPartialOpportunitiesLoading } = useSWR<AxiosResponse<BasicOpportunitiesResponse>>(
    () => getOpportunitiesFilterParamsKey(0, true),
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const { data: universityCommunitiesResponse, isLoading: isUniversityCommunitiesLoading } = useSWR<AxiosResponse<BasicStudentSocietiesResponse>>(
    () =>
      candidateUniversityId &&
      (typeof university_ids === 'undefined' || university_ids === candidateUniversityId || university_ids.includes(candidateUniversityId))
        ? getSocietiesFilterParamsKey(null, false, { university_ids: candidateUniversityId })
        : null,
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const {
    data: communitiesResponse,
    isLoading: isCommunitiesLoading,
    isValidating: isCommunitiesValidating,
    size: communitiesSize,
    setSize: setCommunitiesSize,
  } = useSWRInfinite<AxiosResponse<BasicStudentSocietiesResponse>>(
    (pageIndex) => getSocietiesFilterParamsKey(pageIndex, false, { except_university_ids: candidateUniversityId }),
    apiInstance.get,
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    }
  );

  const { data: partialStudentSocietiesResponse, isLoading: isPartialStudentSocietiesLoading } = useSWR<AxiosResponse<BasicStudentSocietiesResponse>>(
    (pageIndex: number) => getSocietiesFilterParamsKey(pageIndex, true),
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const {
    data: companiesResponse,
    isLoading: isCompaniesLoading,
    isValidating: isCompaniesValidating,
    size: companiesSize,
    setSize: setCompaniesSize,
  } = useSWRInfinite<AxiosResponse<BasicCompaniesResponse>>((pageIndex) => getCompaniesFilterParamsKey(pageIndex, false), apiInstance.get, {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
  });

  const { data: partialCompaniesResponse, isLoading: isPartialCompaniesLoading } = useSWR<AxiosResponse<BasicCompaniesResponse>>(
    (pageIndex: number) => getCompaniesFilterParamsKey(pageIndex, true),
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const {
    featuredOpportunities,
    matchesOpportunities,
    universityEvents,
    opportunities,
    totalOpportunitiesCount,
    allOpportunities,
    isAllOpportunityLoading,
  } = useMemo(() => {
    if (isFeaturedOpportunitiesLoading || isMatchesOpportunitiesLoading || isUniversityEventsLoading || isOpportunitiesLoading)
      return {
        featuredOpportunities: [],
        matchesOpportunities: [],
        universityEvents: [],
        opportunities: [],
        allOpportunities: [],
        totalOpportunitiesCount: 0,
        isAllOpportunityLoading: true,
      };

    const featuredOpportunities = parseAxiosArrayResponseV2(featuredOpportunitiesResponse),
      matchesOpportunities = parseAxiosArrayResponseV2(matchesOpportunitiesResponse),
      universityEvents = parseAxiosArrayResponseV2(universityEventsResponse),
      opportunities = parseAxiosPaginatedResponseV2<BasicOpportunity>(opportunitiesResponse);

    const allOpportunities = [...featuredOpportunities, ...matchesOpportunities, ...universityEvents, ...opportunities];

    const totalOpportunitiesCount =
      featuredOpportunities.length +
      matchesOpportunities.length +
      universityEvents.length +
      getAxiosPaginatedResponseTotalV2<BasicOpportunity>(opportunitiesResponse);

    return {
      featuredOpportunities,
      matchesOpportunities,
      universityEvents,
      opportunities,
      totalOpportunitiesCount,
      allOpportunities,
      isAllOpportunityLoading: false,
    };
  }, [
    isFeaturedOpportunitiesLoading,
    isMatchesOpportunitiesLoading,
    isUniversityEventsLoading,
    isOpportunitiesLoading,
    featuredOpportunitiesResponse,
    matchesOpportunitiesResponse,
    universityEventsResponse,
    opportunitiesResponse,
  ]);

  const isInitialLoading = initialQueryParamsReplaceLoading || isInitialAuthLoading;
  const noOpportunities = !isInitialLoading && !isAllOpportunityLoading && totalOpportunitiesCount === 0;

  const { universityCommunities, communities, totalCommunitiesCount } = useMemo(() => {
    if (isCommunitiesLoading || isUniversityCommunitiesLoading) return { universityCommunities: [], communities: [], totalCommunitiesCount: 0 };

    const universityCommunities = parseArrayResponse(universityCommunitiesResponse?.data);
    const communities = parseAxiosPaginatedResponse(communitiesResponse);

    const totalCommunitiesCount = universityCommunities.length + getAxiosPaginatedResponseTotal<TBasicStudentSociety>(communitiesResponse);

    return { universityCommunities, communities, totalCommunitiesCount };
  }, [isCommunitiesLoading, isUniversityCommunitiesLoading, communitiesResponse, universityCommunitiesResponse]);

  const noCommunities = !isInitialLoading && !(isCommunitiesLoading || isUniversityCommunitiesLoading) && totalCommunitiesCount === 0;

  const { companies, totalCompaniesCount } = useMemo(() => {
    return {
      companies: parseAxiosPaginatedResponse(companiesResponse),
      totalCompaniesCount: getAxiosPaginatedResponseTotal(companiesResponse),
    };
  }, [companiesResponse]);

  const noCompanies = isInitialLoading && !isCompaniesLoading && totalCompaniesCount === 0;

  const hasMoreOpportunities = useMemo(() => {
    const opportunitiesCount = getAxiosPaginatedResponseTotalV2<BasicOpportunity>(opportunitiesResponse);
    return opportunitiesCount > PER_PAGE_COUNT_VERTICAL * opportunitiesSize;
  }, [opportunitiesResponse, opportunitiesSize]);

  const hasMoreCommunities = useMemo(() => {
    const communitiesCount = getAxiosPaginatedResponseTotal<TBasicStudentSociety>(communitiesResponse);
    return communitiesCount > PER_PAGE_COUNT_GRID * communitiesSize;
  }, [communitiesResponse, communitiesSize]);

  const hasMoreCompanies = useMemo(() => {
    return totalCompaniesCount > PER_PAGE_COUNT_GRID * companiesSize;
  }, [totalCompaniesCount, companiesSize]);

  const nextOpportunityPage = useCallback(() => {
    if (!hasMoreOpportunities || isOpportunitiesLoading || isOpportunitiesValidating) return;
    setOpportunitiesSize(opportunitiesSize + 1);
  }, [opportunitiesSize, hasMoreOpportunities, isOpportunitiesLoading, isOpportunitiesValidating]);

  const nextCommunityPage = useCallback(() => {
    if (!hasMoreCommunities || isCommunitiesLoading || isCommunitiesValidating) return;
    setCommunitiesSize(communitiesSize + 1);
  }, [communitiesSize, hasMoreCommunities, isCommunitiesLoading, isCommunitiesValidating]);

  const nextCompanyPage = useCallback(() => {
    if (!hasMoreCompanies || isCompaniesLoading || isCompaniesValidating) return;
    setCompaniesSize(companiesSize + 1);
  }, [companiesSize, hasMoreCompanies, isCompaniesLoading, isCompaniesValidating]);

  const { count: filteredRecordsCount, isLoading: isFilteredRecordsLoading } = useMemo(() => {
    switch (type) {
      case SearchPageType.JOBS:
      case SearchPageType.INTERNSHIPS:
      case SearchPageType.EVENTS:
        return { count: getAxiosResponseTotalV2(partialOpportunitiesResponse), isLoading: isPartialOpportunitiesLoading };
      case SearchPageType.COMMUNITIES:
        return { count: getAxiosResponseTotalV2(partialStudentSocietiesResponse), isLoading: isPartialStudentSocietiesLoading };
      case SearchPageType.COMPANIES:
        return { count: getAxiosResponseTotalV2(partialCompaniesResponse), isLoading: isPartialCompaniesLoading };
      default:
        return { count: 0, isLoading: false };
    }
  }, [
    partialOpportunitiesResponse,
    isPartialOpportunitiesLoading,
    partialStudentSocietiesResponse,
    isPartialStudentSocietiesLoading,
    partialCompaniesResponse,
    isPartialCompaniesLoading,
  ]);

  const firstPageOpportunities = useMemo(() => {
    if (isOpportunitiesLoading || !opportunitiesResponse) return [];
    return parseAxiosArrayResponseV2(opportunitiesResponse[0]);
  }, [opportunitiesResponse, isOpportunitiesLoading]);

  const firstOpportunityPage = useMemo(() => {
    if (isFeaturedOpportunitiesLoading || isFeaturedOpportunitiesLoading || isUniversityEventsLoading || isOpportunitiesLoading) return null;
    const allOpportunities = [...featuredOpportunities, ...matchesOpportunities, ...universityEvents, ...firstPageOpportunities];
    return allOpportunities[0];
  }, [
    featuredOpportunitiesResponse,
    isFeaturedOpportunitiesLoading,
    matchesOpportunitiesResponse,
    isMatchesOpportunitiesLoading,
    universityEventsResponse,
    isUniversityCommunitiesLoading,
    isOpportunitiesLoading,
    firstPageOpportunities,
  ]);

  const selectedOpportunityId = allOpportunities[currentOpportunityIndex]?.id;
  const selectedOpportunityPath = !screens.sm && selectedOpportunityId ? `${API_ROUTES.CAMPAIGNS}/${selectedOpportunityId}` : null;
  const {
    data: selectedOpportunityResponse,
    isLoading: isSelectedOpportunityLoading,
    mutate: mutateSelectedOpportunityResponse,
  } = useSWR<AxiosResponse<OpportunityResponse>>(selectedOpportunityPath, apiInstance.get);

  const selectedOpportunity = useMemo(() => {
    if (isSelectedOpportunityLoading) return null;

    return parseResponse(selectedOpportunityResponse?.data);
  }, [isSelectedOpportunityLoading, selectedOpportunityResponse]);

  const onSelectOpportunity = useCallback(
    (opportunity: BasicOpportunity) => {
      const opportunityIndex = allOpportunities.findIndex((o) => o.id === opportunity.id);
      setCurrentOpportunityIndex(opportunityIndex);
    },
    [allOpportunities]
  );

  useEffect(() => {
    setCurrentOpportunityIndex(0);
  }, [type, firstOpportunityPage]);

  const hasPreviousOpportunity = useMemo(() => {
    return currentOpportunityIndex > 0 && allOpportunities.length > 0;
  }, [currentOpportunityIndex, allOpportunities]);

  const hasNextOpportunity = useMemo(() => {
    return currentOpportunityIndex < allOpportunities.length - 1;
  }, [currentOpportunityIndex, allOpportunities]);

  const handleNextOpportunity = useCallback(() => {
    if (!hasNextOpportunity) return;
    setCurrentOpportunityIndex((index) => index + 1);
  }, [hasNextOpportunity]);

  const handlePreviousOpportunity = useCallback(() => {
    if (!hasPreviousOpportunity) return;
    setCurrentOpportunityIndex((index) => index - 1);
  }, [hasPreviousOpportunity]);

  const onUpdateSelectedOpportunity = useCallback(
    (record: OpportunityAttributesUpdateRecord) => {
      mutateSelectedOpportunityResponse(
        // @ts-ignore TODO: Fix following type in OpportunityAttributes
        (oldOpportunityResponse) => {
          if (!oldOpportunityResponse) return oldOpportunityResponse;

          const newOpportunityData = { ...oldOpportunityResponse.data.data.attributes, ...record };

          return {
            ...oldOpportunityResponse,
            data: {
              ...oldOpportunityResponse.data,
              data: {
                ...oldOpportunityResponse.data.data,
                attributes: newOpportunityData,
              },
            },
          };
        },
        {
          revalidate: false,
        }
      );
    },
    [mutateSelectedOpportunityResponse]
  );

  return {
    pageTitle,
    featuredOpportunities,
    isFeaturedOpportunitiesLoading,
    matchesOpportunities,
    isMatchesOpportunitiesLoading,
    universityEvents,
    isUniversityEventsLoading,
    opportunities,
    isOpportunitiesLoading,
    isOpportunitiesValidating,
    hasMoreOpportunities,
    totalOpportunitiesCount,
    noOpportunities,
    selectedOpportunity,
    hasPreviousOpportunity,
    hasNextOpportunity,
    isSelectedOpportunityLoading,
    isInitialLoading,
    onUpdateSelectedOpportunity,
    handlePreviousOpportunity,
    handleNextOpportunity,
    onSelectOpportunity,
    nextOpportunityPage,
    universityCommunities,
    isUniversityCommunitiesLoading,
    communities,
    isCommunitiesLoading,
    isCommunitiesValidating,
    hasMoreCommunities,
    totalCommunitiesCount,
    noCommunities,
    nextCommunityPage,
    companies,
    isCompaniesLoading,
    isCompaniesValidating,
    hasMoreCompanies,
    totalCompaniesCount,
    noCompanies,
    nextCompanyPage,
    isFilteredRecordsLoading,
    filteredRecordsCount,
  };
};

export type UseGlobalSearchDataReturnType = ReturnType<typeof useGlobalSearchDataInternal>;
